import { SimpleSidebarHeader } from "@volca/ui/components/ui/simple-sidebar";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import {
  DetailSidebarFallback,
  SectionHeading,
  StatusBadge,
  ZonedDateTime,
} from "@/accounts-payable/common";
import { useGetEmail, useGetInvoicesByIds } from "@/accounts-payable/query";
import { useReceiptsEnabled } from "@/accounts-payable/use-receipts-enabled";
import { EMAIL_STATUS } from "@/accounts-payable/utils";
import { EmailAttachmentRow } from "./email-attachment-row";

/**
 * Email detail rendered inside the shared SimpleSidebar (not a bespoke drawer).
 * Fetches the email by id, so deep links resolve regardless of which pages the
 * paginated list has loaded.
 */
export const ApEmailDetail = ({
  emailId,
  businessId,
  onClose,
}: {
  emailId: string;
  businessId: string | null;
  onClose: () => void;
}) => {
  const { data: email, isPending } = useGetEmail(businessId, emailId);
  const receiptsEnabled = useReceiptsEnabled();
  const { invoices, isLoading: invoicesLoading } = useGetInvoicesByIds(
    businessId,
    email?.attachments.flatMap((a) => a.invoice_ids) ?? [],
  );

  if (!email) {
    return (
      <DetailSidebarFallback
        title="Email"
        loading={isPending}
        errorMessage="Couldn’t load the email. Please try again."
        onClose={onClose}
      />
    );
  }

  return (
    <Stack align="start" gap={0} className="h-full w-full">
      {/* The header cuts a long subject to one line; the tooltip has it all. */}
      <SimpleSidebarHeader
        title={<span title={email.subject}>{email.subject}</span>}
        description={
          <>
            From {email.from} · <ZonedDateTime value={email.received_at} />
          </>
        }
        actions={<StatusBadge meta={EMAIL_STATUS[email.status]} />}
        onClose={onClose}
      />

      {/* Body */}
      <div className="min-h-0 w-full flex-1 overflow-y-auto">
        {/* Attachments lead: they hold the outcome and the one action (open
            the invoice). The message is read-only background, so it sits
            below on a quiet grey surface. */}
        <Stack align="start" gap={8} className="w-full px-5 py-6">
          <Stack className="w-full" align="start" gap={2}>
            <SectionHeading>
              Attachments ({email.attachments.length})
            </SectionHeading>
            {/* Plain rows, no cards inside the panel; the file icons mark
                where each attachment starts. */}
            <Stack className="w-full" align="start" gap={6}>
              {email.attachments.map((attachment) => (
                <EmailAttachmentRow
                  key={attachment.id}
                  attachment={attachment}
                  invoices={invoices}
                  receiptsEnabled={receiptsEnabled}
                  invoicesLoading={invoicesLoading}
                />
              ))}
            </Stack>
          </Stack>

          {email.message && (
            <Stack className="w-full" align="start" gap={2}>
              <SectionHeading>Message</SectionHeading>
              <Stack
                align="start"
                gap={0}
                className="w-full rounded-md bg-neutral p-4"
              >
                <Text
                  tone="muted"
                  className="leading-relaxed whitespace-pre-wrap"
                >
                  {email.message}
                </Text>
              </Stack>
            </Stack>
          )}
        </Stack>
      </div>
    </Stack>
  );
};
